import { ArrowRightIcon } from '@velocity/icons/system';
import { Button, Headline } from '@velocity/ui';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import isAuthenticated from '../../../redux/auth/authUtils';
import { Feature } from '../../../redux/feature-toggles/types';
import {
  closeFeedbackForm,
  openFeedbackForm,
  saveFeedback,
} from '../../../redux/feedback/feedback-slice';
import { EDevice } from '../../../redux/sequence/enums';
import { useAppDispatch } from '../../../redux/store';
import { RootState } from '../../../redux/types';
import Modal from '../../b2b/components/modal/modal';
import { ReactComponent as FeedbackIconMobile } from '../icons/feedback-icon-mobile.svg';
import ChipSelect from './components/chip-select';
import FeedbackTextArea from './components/feedback-textarea';
import MultiSelectDropdown from './components/multi-select-dropdown';
import OptionGroup from './components/option-group';
import ThankYou from './components/thank-you';
import {
  ageGroupOptions,
  ageGroupQuestion,
  buildAnswerList,
  collectBrowserData,
  easeOfUseCommentPlaceholder,
  easeOfUseCommentValues,
  easeOfUseOptions,
  easeOfUseQuestion,
  FeedbackFormValues,
  feedbackDefaultValues,
  feedbackErrors,
  feedbackText,
  isOneOf,
  recommendationOptions,
  recommendationPositiveValues,
  recommendationQuestion,
  unclearCategoryOptions,
  unclearMomentDetailValues,
  unclearMomentOptions,
  unclearMomentQuestion,
} from './feedback-config';
import './feedback.css';

const LAST_STEP = 2;
const THANK_YOU_STEP = 3;

const stepFields: (keyof FeedbackFormValues)[][] = [
  ['easeOfUse', 'easeOfUseComment', 'unclearMoment', 'unclearComment'],
  ['ageGroup'],
  ['recommendation', 'recommendationComment'],
];

const questionTitleClass =
  'mb-3! headline-200 text-(--ayvens-theme-brand-secondary-400)';

const FeedbackSurvey: React.FC = () => {
  const { device } = useSelector((state: RootState) => state.sequence);
  const dispatch = useAppDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const { feedbackForm } = useSelector((state: RootState) => state.feedback);
  const { [Feature.Feedback]: feedback } = useSelector(
    (state: RootState) => state.featureToggles,
  );

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const {
    watch,
    setValue,
    getValues,
    register,
    trigger,
    setError,
    clearErrors,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FeedbackFormValues>({
    mode: 'onTouched',
    defaultValues: feedbackDefaultValues,
  });

  // Custom (non-input) single-select fields are registered programmatically so
  // that validation and step-level `trigger()` work with the pill buttons.
  useEffect(() => {
    register('easeOfUse', { required: feedbackErrors.selectOption });
    register('unclearMoment', { required: feedbackErrors.selectOption });
    register('unclearCategories');
    register('ageGroup', { required: feedbackErrors.ageGroup });
    register('recommendation', { required: feedbackErrors.selectOption });
  }, [register]);

  // Start every session from a clean slate, whatever way the modal was closed
  // before (close button, backdrop click or the thank-you screen).
  useEffect(() => {
    if (!feedbackForm.isOpened) return;
    setStep(0);
    setLoading(false);
    reset(feedbackDefaultValues);
    clearErrors();
  }, [feedbackForm.isOpened, reset, clearErrors]);

  const isMobile = device === EDevice.MOBILE;

  const easeOfUse = watch('easeOfUse');
  const unclearMoment = watch('unclearMoment');
  const unclearCategories = watch('unclearCategories');
  const recommendation = watch('recommendation');

  const easeShowsComment = isOneOf(easeOfUseCommentValues, easeOfUse);
  const unclearShowsDetails = isOneOf(unclearMomentDetailValues, unclearMoment);
  const recommendationPositive = isOneOf(
    recommendationPositiveValues,
    recommendation,
  );

  const handleEaseChange = (value: string) => {
    setValue('easeOfUse', value, { shouldValidate: true });
    if (!isOneOf(easeOfUseCommentValues, value)) {
      setValue('easeOfUseComment', '');
      clearErrors('easeOfUseComment');
    }
  };

  const handleUnclearChange = (value: string) => {
    setValue('unclearMoment', value, { shouldValidate: true });
    if (!isOneOf(unclearMomentDetailValues, value)) {
      setValue('unclearComment', '');
      setValue('unclearCategories', []);
      clearErrors('unclearComment');
    }
  };

  const goNext = async () => {
    // Guard required step answers explicitly before running full field validation.
    if (step === 0) {
      let hasRequiredError = false;
      if (!getValues('easeOfUse')) {
        setError('easeOfUse', {
          type: 'required',
          message: feedbackErrors.selectOption,
        });
        hasRequiredError = true;
      }
      if (!getValues('unclearMoment')) {
        setError('unclearMoment', {
          type: 'required',
          message: feedbackErrors.selectOption,
        });
        hasRequiredError = true;
      }
      if (hasRequiredError) return;
    }

    if (step === 1 && !getValues('ageGroup')) {
      setError('ageGroup', {
        type: 'required',
        message: feedbackErrors.ageGroup,
      });
      return;
    }

    if (step === 2 && !getValues('recommendation')) {
      setError('recommendation', {
        type: 'required',
        message: feedbackErrors.selectOption,
      });
      return;
    }

    const valid = await trigger(stepFields[step]);
    if (valid) setStep((current) => current + 1);
  };

  const close = () => {
    dispatch(closeFeedbackForm());
    setStep(0);
    setLoading(false);
    reset(feedbackDefaultValues);
    clearErrors();
  };

  const onSubmit = async (values: FeedbackFormValues) => {
    const answerList = buildAnswerList(values);
    const browserData = await collectBrowserData();
    setLoading(true);
    dispatch(
      saveFeedback({
        accessToken: auth.accessToken,
        answer_list: answerList,
        browser_data: browserData,
      }),
    )
      .unwrap()
      .then(() => {
        reset(feedbackDefaultValues);
        clearErrors();
        setStep(THANK_YOU_STEP);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  };

  if (!feedback.enabled || !isAuthenticated(auth)) return null;

  return (
    <div className='feedback'>
      {isMobile ? (
        <div className='feedback-opener-mobile'>
          <FeedbackIconMobile onClick={() => dispatch(openFeedbackForm())} />
        </div>
      ) : (
        <div className='feedback-opener'>
          <button
            type='button'
            data-testid='feedback-opener'
            onClick={() => dispatch(openFeedbackForm())}
            className='bg-(--vel-color-secondary-500) px-5! py-2! text-sm! font-bold text-white'
          >
            Feedback
          </button>
        </div>
      )}

      <Modal
        isOpen={feedbackForm.isOpened}
        isMobile={isMobile}
        onClose={close}
        type='feedback'
        direction='up'
        background='transparent'
        closeButtonVisibility={step !== THANK_YOU_STEP}
        showCloseButtonForMobile={true}
        className='feedback-modal'
      >
        {step === THANK_YOU_STEP ? (
          <ThankYou onClose={close} isMobile={isMobile} />
        ) : (
          <>
            <Headline
              variant={isMobile ? '300' : '400'}
              component='h1'
              className='text-(--ayvens-theme-brand-secondary-400)!'
            >
              {feedbackText.title}
              {step === 0 && (
                <p
                  className={`${isMobile ? 'text-loose-200' : 'text-loose-300'} text-(--ayvens-theme-brand-secondary-400)`}
                >
                  Mit deinem Feedback können wir unseren Service und den
                  Leasingprozess weiter verbessern.
                </p>
              )}
            </Headline>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className='feedback-container'>
                {step === 0 && (
                  <>
                    {/* Question 1 */}
                    <section>
                      <p className={questionTitleClass}>
                        {easeOfUseQuestion.title}
                      </p>
                      <OptionGroup
                        name='ease'
                        options={easeOfUseOptions}
                        value={easeOfUse}
                        onChange={handleEaseChange}
                        stacked={isMobile}
                        error={errors.easeOfUse?.message}
                      />
                      {easeShowsComment && (
                        <FeedbackTextArea
                          placeholder={easeOfUseCommentPlaceholder}
                          error={errors.easeOfUseComment?.message}
                          {...register('easeOfUseComment', {
                            validate: (value) =>
                              !isOneOf(
                                easeOfUseCommentValues,
                                getValues('easeOfUse'),
                              ) ||
                              value.trim().length > 0 ||
                              feedbackErrors.easeComment,
                          })}
                        />
                      )}
                    </section>

                    {/* Question 2 */}
                    <section>
                      <p className={questionTitleClass}>
                        {unclearMomentQuestion.title}
                      </p>
                      <OptionGroup
                        name='unclear'
                        options={unclearMomentOptions}
                        value={unclearMoment}
                        onChange={handleUnclearChange}
                        stacked={false}
                        error={errors.unclearMoment?.message}
                      />
                      {unclearShowsDetails && (
                        <>
                          <p className='headline-140 mt-4! mb-3! text-(--ayvens-theme-brand-secondary-400)'>
                            {unclearMomentQuestion.categoryTitle}
                          </p>
                          {isMobile ? (
                            <MultiSelectDropdown
                              options={unclearCategoryOptions}
                              value={unclearCategories}
                              onChange={(cats) =>
                                setValue('unclearCategories', cats)
                              }
                            />
                          ) : (
                            <ChipSelect
                              options={unclearCategoryOptions}
                              value={unclearCategories}
                              onChange={(cats) =>
                                setValue('unclearCategories', cats)
                              }
                            />
                          )}
                          <FeedbackTextArea
                            placeholder={
                              unclearMomentQuestion.commentPlaceholder
                            }
                            error={errors.unclearComment?.message}
                            {...register('unclearComment', {
                              validate: (value) =>
                                !isOneOf(
                                  unclearMomentDetailValues,
                                  getValues('unclearMoment'),
                                ) ||
                                value.trim().length > 0 ||
                                feedbackErrors.unclearComment,
                            })}
                          />
                        </>
                      )}
                    </section>
                  </>
                )}

                {step === 1 && (
                  <section>
                    <p className={questionTitleClass}>
                      {ageGroupQuestion.title}
                    </p>
                    <OptionGroup
                      name='age'
                      options={ageGroupOptions}
                      value={watch('ageGroup')}
                      onChange={(value) =>
                        setValue('ageGroup', value, { shouldValidate: true })
                      }
                      stacked={isMobile}
                      error={errors.ageGroup?.message}
                    />
                  </section>
                )}

                {step === 2 && (
                  <section>
                    <p className={questionTitleClass}>
                      {recommendationQuestion.title}
                    </p>
                    <OptionGroup
                      name='recommendation'
                      options={recommendationOptions}
                      value={recommendation}
                      onChange={(value) =>
                        setValue('recommendation', value, {
                          shouldValidate: true,
                        })
                      }
                      stacked={isMobile}
                      error={errors.recommendation?.message}
                    />
                    {recommendation && (
                      <FeedbackTextArea
                        placeholder={
                          recommendationPositive
                            ? recommendationQuestion.positivePlaceholder
                            : recommendationQuestion.negativePlaceholder
                        }
                        {...register('recommendationComment')}
                      />
                    )}
                  </section>
                )}
              </div>

              <div className='feedback-footer'>
                <div className='flex items-center justify-between'>
                  {step < LAST_STEP ? (
                    <Button
                      variant='secondary-filled'
                      size='m'
                      data-testid='feedback-next'
                      onClick={goNext}
                    >
                      {feedbackText.next}
                    </Button>
                  ) : (
                    <Button
                      variant='secondary-filled'
                      size='m'
                      data-testid='feedback-submit'
                      StartIcon={ArrowRightIcon}
                      loading={loading}
                      onClick={handleSubmit(onSubmit)}
                    >
                      {feedbackText.submit}
                    </Button>
                  )}
                  <div
                    className={`${isMobile ? 'text-loose-100' : 'text-loose-200'} right pt-7!`}
                  >
                    Schritt {step + 1} | {LAST_STEP + 1}
                  </div>
                </div>
              </div>
            </form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default FeedbackSurvey;
